import { Space, Tooltip, Typography, theme } from 'antd';
import { FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../../lib/api';
import { PermissionButton } from '../../../components/common/PermissionButton';
import DocumentCreateModal from '../../../components/document/DocumentCreateModal';

type DocumentPreviewItem = {
  document: {
    id: string;
    title: string;
    category?: string | null;
  };
  documentType?: {
    id: string;
    name?: string | null;
    category?: string | null;
  } | null;
};

export type LocationDocumentsSectionProps = {
  entityId: string | null;
  allowOfficial: boolean;
  onOpenDocument: (documentId: string) => void;
};

export function LocationDocumentsSection({
  entityId,
  allowOfficial,
  onOpenDocument,
}: LocationDocumentsSectionProps) {
  const { Text } = Typography;
  const { token } = theme.useToken();
  const [createOpen, setCreateOpen] = useState(false);
  const canCreate = Boolean(entityId);

  const listInput = useMemo(
    () => ({
      entityType: 'location' as const,
      entityId: entityId ?? '',
      page: 1,
      limit: 50,
    }),
    [entityId]
  );

  const { data, isLoading } = trpc.document.list.useQuery(listInput, {
    enabled: Boolean(entityId),
  });

  const rows = ((data?.items ?? []) as DocumentPreviewItem[])
    .slice()
    .sort((a, b) => (a.document.title ?? '').localeCompare(b.document.title ?? '', 'ja'));

  const hasDocuments = rows.length > 0;

  useEffect(() => {
    if (!canCreate && createOpen) {
      setCreateOpen(false);
    }
  }, [canCreate, createOpen]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <Space size={8}>
          <FileTextOutlined style={{ color: token.colorWarning }} />
          <Text strong style={{ fontSize: 14 }}>書類</Text>
        </Space>
        <Space size={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {hasDocuments ? rows.length : 0}件
          </Text>
          <Tooltip title={!canCreate ? 'エリアを選択してください' : undefined}>
            <span>
              <PermissionButton
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
                disabled={!canCreate}
                requiredPermission="manage"
              >
                追加
              </PermissionButton>
            </span>
          </Tooltip>
        </Space>
      </div>
      {!canCreate ? (
        <Text type="secondary">エリアを選択してください</Text>
      ) : isLoading ? (
        <Text type="secondary">読み込み中...</Text>
      ) : !hasDocuments ? (
        <Text type="secondary">書類はまだありません</Text>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 72px)',
            justifyContent: 'start',
            gap: 4,
          }}
        >
          {rows.map((row) => {
            const color = row.document.category === 'official' ? token.colorError : token.colorWarning;
            return (
              <div
                key={row.document.id}
                style={{
                  padding: 0,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textAlign: 'center',
                }}
                onClick={() => onOpenDocument(row.document.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 16,
                      background: token.colorBgContainer,
                      border: `1px solid ${token.colorBorderSecondary}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    }}
                  >
                    <FileTextOutlined style={{ fontSize: 24 }} />
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.2,
                    maxWidth: 72,
                    margin: '0 auto',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                  title={row.document.title}
                >
                  {row.document.title}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <DocumentCreateModal
        open={createOpen && canCreate}
        entityType="location"
        entityId={entityId ?? ''}
        allowOfficial={allowOfficial}
        allowBuilding={allowOfficial}
        restrictToEntityType={true}
        onCancel={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
        }}
      />
    </>
  );
}
